import { AppHomeScreen } from "@components/brand/AppHomeScreen";
import { Logo, LogoMark } from "@components/brand/Logo";
import { PartnerLogo } from "@components/brand/PartnerLogo";
import { PhoneFrame } from "@components/brand/PhoneFrame";
import { ValueLine } from "@components/brand/ValueLine";
import { AppCta } from "@components/home/AppCta";
import { Faq } from "@components/home/Faq";
import { Page } from "@components/layout/Page";
import { FormBlock } from "@components/product/Blocks";
import { ProductIcon } from "@components/shared/ProductIcon";
import { Section, SectionHeading } from "@components/shared/Section";
import { Steps } from "@components/shared/Steps";
import { StoreBadges } from "@components/shared/StoreBadges";
import { PARTNERS, Partner } from "@/lib/partners";
import { CONTACT, FLEX, UNLOCK } from "@/lib/site";
import { cn } from "@/ui/utils/cn";
import { useTranslations } from "@services/strings";
import { Check, FileX2, Lock, Merge, Zap } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";

type Lang = "en" | "ar";

/** Interpolation values shared by every partner-aware string on the page. */
type Vars = { partner: string; compounds: string; maxYears: number };

type PartnerProps = { partner?: Partner; lang: Lang; vars: Vars };

type Translate = ReturnType<typeof useTranslations>["t"];

const flexVars = (partner: Partner | undefined, lang: Lang): Vars => ({
  partner: partner ? partner.name[lang] : "",
  compounds: partner ? partner.compounds[lang].join(" · ") : "",
  maxYears: UNLOCK.maxYears,
});

/** The copy that differs between the generic page and a developer's page. */
const flexCopy = (partner: Partner | undefined, vars: Vars, t: Translate) =>
  partner
    ? {
        slug: partner.slug,
        title: t("site.flex.partnerMeta.title", vars),
        description: t("site.flex.partnerMeta.description", vars),
        ctaBody: t("site.flex.cta.body", vars),
        // The partner list sits between the steps and the FAQ only on the
        // generic page, so the alternating backgrounds shift by one there.
        faqTone: "white" as const,
        formTone: "mist" as const,
      }
    : {
        slug: "generic",
        title: t("site.flex.meta.title"),
        description: t("site.flex.meta.description"),
        ctaBody: t("site.home.cta.body"),
        faqTone: "mist" as const,
        formTone: "white" as const,
      };

/**
 * Co-brand lockup for a partner page: the developer's own mark first (they
 * are the relationship the homeowner trusts), MNZL as the partner making it
 * possible. Both marks are dark-on-light, so the lockup sits on a white tile.
 */
const PartnerLockup = ({ partner, lang }: { partner: Partner; lang: Lang }) => (
  <div className="inline-flex items-center gap-4 rounded-2xl bg-white px-5 py-3.5">
    <PartnerLogo partner={partner} lang={lang} className="h-7 md:h-8" />
    <span className="h-7 w-px bg-slate-200" aria-hidden="true" />
    <Logo height={18} className="shrink-0 text-ink" />
  </div>
);

const GenericBadge = ({ label }: { label: string }) => (
  <span className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 py-1.5 pe-4 ps-1.5 text-sm font-semibold text-white">
    <span className="grid h-8 w-8 place-items-center rounded-full bg-mint text-ink">
      <LogoMark size={16} />
    </span>
    {label}
  </span>
);

/** Partner lockup plus the "exclusively for…" line, or the generic MNZL badge. */
const HeroBadge = ({ partner, lang, vars }: PartnerProps) => {
  const { t } = useTranslations();

  if (!partner) {
    return <GenericBadge label={t("site.flex.genericBadge")} />;
  }

  return (
    <>
      <PartnerLockup partner={partner} lang={lang} />
      <p className="mt-5 text-sm font-semibold text-mint">
        {t("site.flex.badge", vars)}
      </p>
    </>
  );
};

/** Trust first: who this is for, the promise, the app on a phone. */
const Hero = ({
  partner,
  lang,
  vars,
  slug,
}: PartnerProps & { slug: string }) => {
  const { t } = useTranslations();
  const subtitle = partner
    ? t("site.flex.hero.subtitle", vars)
    : t("site.flex.hero.genericSubtitle");
  const property = partner
    ? { name: partner.sampleUnit[lang], developer: partner.name[lang] }
    : undefined;

  return (
    <section className="relative overflow-hidden bg-navy text-white">
      <ValueLine className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] w-full opacity-70 rtl:-scale-x-100" />
      <div className="container relative grid items-center gap-12 py-14 md:grid-cols-[1.1fr_0.9fr] md:py-24">
        <div className="max-w-xl">
          <HeroBadge partner={partner} lang={lang} vars={vars} />
          <h1 className="display mt-6 text-display-sm md:text-display">
            {t("site.flex.hero.title")}
          </h1>
          <p className="mt-6 max-w-prose text-body text-slate-300 md:text-lg md:leading-relaxed">
            {subtitle}
          </p>
          <StoreBadges placement={`flex-hero-${slug}`} className="mt-8" />
        </div>
        <div className="flex justify-center md:justify-end">
          <PhoneFrame>
            <AppHomeScreen animate={false} property={property} />
          </PhoneFrame>
        </div>
      </div>
    </section>
  );
};

/** The one instruction that matters on a partner page: sign up with the number on file. */
const PhoneKeyBand = ({ vars }: { vars: Vars }) => {
  const { t } = useTranslations();
  return (
    <section className="bg-mint-soft">
      <div className="container flex flex-col gap-3 py-8 md:flex-row md:items-center md:gap-10 md:py-10">
        <p className="shrink-0 text-h3 font-semibold text-ink">
          {t("site.flex.phoneKey.title")}
        </p>
        <p className="max-w-2xl text-body text-slate-700">
          {t("site.flex.phoneKey.body", vars)}
        </p>
      </div>
    </section>
  );
};

const SEE_ITEMS = ["property", "progress", "next", "value"] as const;

const WhatYouSee = () => {
  const { t } = useTranslations();
  return (
    <Section tone="white">
      <div className="grid gap-10 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
        <SectionHeading title={t("site.flex.see.title")} />
        <ul className="grid gap-4 sm:grid-cols-2">
          {SEE_ITEMS.map((id) => (
            <li
              key={id}
              className="flex items-start gap-3 text-body text-slate-700"
            >
              <span className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-mint-soft text-mint-ink">
                <Check size={14} aria-hidden="true" />
              </span>
              {t(`site.flex.see.items.${id}`)}
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
};

const PayLessSoon = ({
  partner,
  vars,
}: Pick<PartnerProps, "partner" | "vars">) => {
  const { t } = useTranslations();
  if (FLEX.payLessLive) {
    return null;
  }
  return (
    <p className="mt-4 inline-block rounded-full bg-lavender px-3 py-1 text-sm font-semibold text-lavender-ink">
      {partner
        ? t("site.flex.do.payLess.soon", vars)
        : t("site.common.comingSoon")}
    </p>
  );
};

/** The two products a Flex home opens up, as two tiles. */
const WhatYouCanDo = ({
  partner,
  vars,
}: Pick<PartnerProps, "partner" | "vars">) => {
  const { t } = useTranslations();
  return (
    <Section tone="mist">
      <SectionHeading title={t("site.flex.do.title")} />
      <div className="mt-10 grid gap-5 md:grid-cols-2">
        <Link
          href="/unlock"
          className="rounded-3xl bg-white p-7 transition-shadow hover:shadow-panel md:p-9"
        >
          <ProductIcon product="unlock" />
          <h3 className="display mt-4 text-2xl md:text-3xl">
            {t("site.flex.do.unlock.title")}
          </h3>
          <p className="mt-3 text-body text-slate-700">
            {t("site.flex.do.unlock.body")}
          </p>
        </Link>
        <Link
          href="/pay-less"
          className={cn(
            "rounded-3xl bg-white p-7 transition-shadow hover:shadow-panel md:p-9",
            !FLEX.payLessLive && "border border-dashed border-slate-300",
          )}
        >
          <ProductIcon product="payLess" />
          <h3 className="display mt-4 text-2xl md:text-3xl">
            {t("site.flex.do.payLess.title")}
          </h3>
          <p className="mt-3 text-body text-slate-700">
            {t("site.flex.do.payLess.body", vars)}
          </p>
          <PayLessSoon partner={partner} vars={vars} />
        </Link>
      </div>
    </Section>
  );
};

const WHY_ITEMS = [
  { id: "documents", Icon: FileX2 },
  { id: "speed", Icon: Zap },
  { id: "one", Icon: Merge },
  { id: "privacy", Icon: Lock },
] as const;

const WhyFlex = () => {
  const { t } = useTranslations();
  return (
    <Section tone="white">
      <div className="grid gap-10 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
        <SectionHeading title={t("site.flex.why.title")} />
        <dl className="grid gap-x-10 gap-y-8 sm:grid-cols-2">
          {WHY_ITEMS.map(({ id, Icon }) => (
            <div key={id}>
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-mint-soft text-mint-ink">
                <Icon size={20} aria-hidden="true" />
              </span>
              <dt className="mt-4 text-h3 font-semibold">
                {t(`site.flex.why.items.${id}.title`)}
              </dt>
              <dd className="mt-2 text-body text-slate-600">
                {t(`site.flex.why.items.${id}.body`)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </Section>
  );
};

const STEP_IDS = ["one", "two", "three"] as const;

const HowItWorks = () => {
  const { t } = useTranslations();
  return (
    <Steps
      tone="mist"
      title={t("site.flex.steps.title")}
      steps={STEP_IDS.map((id) => ({
        title: t(`site.flex.steps.items.${id}.title`),
        body: t(`site.flex.steps.items.${id}.body`),
      }))}
    />
  );
};

const HOW_STEPS = ["one", "two", "three", "four", "five", "six"] as const;

const FAQ_IDS = [
  "what",
  "ownership",
  "who",
  "how",
  "value",
  "privacy",
  "documents",
  "contact",
] as const;

/**
 * The questions homeowners ask before they download. Every answer names the
 * developer mid-sentence, so the generic page can say "your developer" instead.
 */
const FlexFaq = ({
  partner,
  vars,
  tone,
}: PartnerProps & { tone: "white" | "mist" }) => {
  const { t } = useTranslations();
  const faqVars = {
    ...vars,
    partner: partner ? vars.partner : t("site.flex.faq.anyDeveloper"),
    compounds: partner ? vars.compounds : t("site.flex.faq.anyCompound"),
    email: CONTACT.email,
  };
  return (
    <Faq
      tone={tone}
      title={t("site.flex.faq.title")}
      items={FAQ_IDS.map((id) => ({
        id,
        question: t(`site.flex.faq.items.${id}.q`),
        answer: t(`site.flex.faq.items.${id}.a`, faqVars),
        ...(id === "how"
          ? {
              steps: HOW_STEPS.map((step) =>
                t(`site.flex.faq.items.how.steps.${step}`, faqVars),
              ),
            }
          : {}),
      }))}
    />
  );
};

/** On the generic page: every developer with a co-branded page of their own. */
const PartnerList = ({ lang }: { lang: Lang }) => {
  const { t } = useTranslations();
  return (
    <Section tone="white">
      <div className="grid gap-10 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
        <SectionHeading
          title={t("site.flex.partnersList.title")}
          lead={t("site.flex.partnersList.body")}
        />
        <ul className="flex flex-col gap-3">
          {PARTNERS.map((item) => (
            <li key={item.slug}>
              <Link
                href={`/flex/${item.slug}`}
                className="flex items-center gap-5 rounded-2xl border border-slate-200 px-5 py-4 transition-colors hover:border-ink"
              >
                <span className="grid h-14 w-32 shrink-0 place-items-center rounded-xl bg-mist px-3">
                  <PartnerLogo
                    partner={item}
                    lang={lang}
                    className="h-auto max-h-5 w-auto max-w-full"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold">
                    {item.name[lang]}
                  </span>
                  <span className="block text-sm text-slate-500">
                    {item.compounds[lang].join(" · ")}
                  </span>
                </span>
                <span className="shrink-0 text-sm font-semibold text-ink">
                  {t("site.common.learnMore")}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
};

/**
 * The MNZL Flex landing, generic or co-branded for one developer. Built for
 * the WhatsApp outreach: trust first, then the one instruction that matters
 * (sign up with the number on file), then the app.
 */
export const FlexLanding = ({ partner }: { partner?: Partner }) => {
  const { t } = useTranslations();
  const { locale } = useRouter();
  const lang: Lang = locale === "ar-EG" ? "ar" : "en";
  const vars = flexVars(partner, lang);
  const copy = flexCopy(partner, vars, t);

  return (
    <Page title={copy.title} description={copy.description} headerTone="navy">
      <Hero partner={partner} lang={lang} vars={vars} slug={copy.slug} />
      {partner ? <PhoneKeyBand vars={vars} /> : null}
      <WhatYouSee />
      <WhatYouCanDo partner={partner} vars={vars} />
      <WhyFlex />
      <HowItWorks />
      {partner ? null : <PartnerList lang={lang} />}
      <FlexFaq partner={partner} lang={lang} vars={vars} tone={copy.faqTone} />
      <FormBlock
        title={t("site.flex.form.title")}
        body={t("site.flex.form.body")}
        interest="flex"
        placement={`flex-${copy.slug}`}
        tone={copy.formTone}
      />
      <AppCta
        placement={`flex-cta-${copy.slug}`}
        title={t("site.flex.cta.title")}
        body={copy.ctaBody}
      />
    </Page>
  );
};
